import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { createEvent } from '../../services/events'
import { getUser } from '../../services/helpers'
import { uploadEventImage } from '../../services/upload'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ArrowLeftIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

const EventFormPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  // Event creation state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    venue: '',
    location: {
      city: '',
      country: ''
    },
    startsAt: '',
    endsAt: '',
    sessions: [],
    ranks: [],
    slots: '',
    coverImage: '',
    status: 'DRAFT',
    publishAt: '',
    discounts: [],
    tags: ''
  })

  // Session form state
  const [sessionForm, setSessionForm] = useState({
    name: '',
    startsAt: '',
    endsAt: ''
  })

  // Discount form state
  const [discountForm, setDiscountForm] = useState({
    type: 'PERCENT',
    value: '',
    expiresAt: ''
  })

  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleEventChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setEventForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setEventForm(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSessionChange = (e) => {
    const { name, value } = e.target
    setSessionForm(prev => ({ ...prev, [name]: value }))
  }

  const addSession = () => {
    if (sessionForm.name && sessionForm.startsAt && sessionForm.endsAt) {
      setEventForm(prev => ({
        ...prev,
        sessions: [...prev.sessions, {
          name: sessionForm.name,
          startsAt: new Date(sessionForm.startsAt).toISOString(),
          endsAt: new Date(sessionForm.endsAt).toISOString()
        }]
      }))
      setSessionForm({ name: '', startsAt: '', endsAt: '' })
    }
  }

  const removeSession = (index) => {
    setEventForm(prev => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index)
    }))
  }

  const handleRankChange = (e) => {
    const value = e.target.value
    if (value && !eventForm.ranks.includes(value)) {
      setEventForm(prev => ({
        ...prev,
        ranks: [...prev.ranks, value]
      }))
      e.target.value = ''
    }
  }

  const removeRank = (index) => {
    setEventForm(prev => ({
      ...prev,
      ranks: prev.ranks.filter((_, i) => i !== index)
    }))
  }

  const handleDiscountChange = (e) => {
    const { name, value } = e.target
    setDiscountForm(prev => ({ ...prev, [name]: value }))
  }

  const addDiscount = () => {
    if (discountForm.type && discountForm.value) {
      setEventForm(prev => ({
        ...prev,
        discounts: [...prev.discounts, {
          type: discountForm.type,
          value: Number(discountForm.value),
          expiresAt: discountForm.expiresAt ? new Date(discountForm.expiresAt).toISOString() : undefined
        }]
      }))
      setDiscountForm({ type: 'PERCENT', value: '', expiresAt: '' })
    }
  }

  const removeDiscount = (index) => {
    setEventForm(prev => ({
      ...prev,
      discounts: prev.discounts.filter((_, i) => i !== index)
    }))
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB')
      return
    }

    setImageFile(file)
    setError('')

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)

    setUploadingImage(true)
    try {
      const res = await uploadEventImage(file)
      if (res.status === 200 && res.data?.imageUrl) {
        setEventForm(prev => ({ ...prev, coverImage: res.data.imageUrl }))
        setSuccess('Image uploaded successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to upload image')
        setImageFile(null)
        setImagePreview(null)
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.')
      setImageFile(null)
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleEventSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')
    
    try {
      const params = new URLSearchParams()
      
      params.append('title', eventForm.title)
      params.append('description', eventForm.description)
      params.append('venue', eventForm.venue)
      params.append('location.city', eventForm.location.city || '')
      params.append('location.country', eventForm.location.country || '')
      params.append('startsAt', new Date(eventForm.startsAt).toISOString())
      params.append('endsAt', new Date(eventForm.endsAt).toISOString())
      params.append('slots', eventForm.slots)
      params.append('status', eventForm.status || 'DRAFT')
      if (eventForm.coverImage) params.append('coverImage', eventForm.coverImage)
      if (eventForm.publishAt) params.append('publishAt', new Date(eventForm.publishAt).toISOString())
      if (eventForm.sessions && eventForm.sessions.length > 0) {
        eventForm.sessions.forEach((session, index) => {
          params.append(`sessions[${index}].name`, session.name)
          params.append(`sessions[${index}].startsAt`, session.startsAt)
          params.append(`sessions[${index}].endsAt`, session.endsAt)
        })
      }
      if (eventForm.ranks && eventForm.ranks.length > 0) {
        eventForm.ranks.forEach(rank => {
          params.append('ranks[]', rank)
        })
      }
      if (eventForm.discounts && eventForm.discounts.length > 0) {
        eventForm.discounts.forEach((discount, index) => {
          params.append(`discounts[${index}].type`, discount.type)
          params.append(`discounts[${index}].value`, discount.value)
          if (discount.expiresAt) params.append(`discounts[${index}].expiresAt`, discount.expiresAt)
        })
      }
      if (eventForm.tags) {
        eventForm.tags.split(',').forEach(tag => {
          if (tag.trim()) params.append('tags[]', tag.trim())
        })
      }
      
      const res = await createEvent(params.toString())
      
      if (res.status === 201) {
        setSuccess('Event created successfully!')
        setTimeout(() => {
          navigate('/center/events')
        }, 1500)
      } else {
        setError(res.error || 'Failed to create event')
      }
    } catch (err) {
      setError('An error occurred while creating event')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'events'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        <div className="p-6 lg:p-8 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/center/events"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Events
            </Link>

            {/* Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                {success}
              </div>
            )}

            <h1 className="text-3xl font-bold text-slate-900">Create Event</h1>
            <p className="text-slate-600 mt-2">Fill in the details to create a new event</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900">Event Details</h2>
              <p className="text-sm text-slate-500">Basic information about your event</p>
            </div>

            <form onSubmit={handleEventSubmit} className="p-6 sm:p-8 space-y-8">
              {/* Basic Info */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Event Title *</label>
                  <input
                    name="title"
                    value={eventForm.title}
                    onChange={handleEventChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                    placeholder="e.g. Annual Tech Conference" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Description *</label>
                  <textarea
                    name="description"
                    rows={4} 
                    value={eventForm.description}
                    onChange={handleEventChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition resize-none" 
                    placeholder="Describe what attendees can expect..." 
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Venue Name *</label>
                    <input
                      name="venue"
                      value={eventForm.venue}
                      onChange={handleEventChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                      placeholder="e.g. National Stadium"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">City</label>
                    <input
                      name="location.city"
                      value={eventForm.location.city || ''} 
                      onChange={handleEventChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                      placeholder="e.g. Lagos"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Start Date & Time *</label>
                    <input
                      type="datetime-local"
                      name="startsAt" 
                      value={eventForm.startsAt}
                      onChange={handleEventChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">End Date & Time *</label>
                    <input
                      type="datetime-local"
                      name="endsAt" 
                      value={eventForm.endsAt}
                      onChange={handleEventChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Capacity (Slots) *</label>
                  <input
                    type="number"
                    name="slots" 
                    value={eventForm.slots}
                    onChange={handleEventChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                    placeholder="100" 
                    required
                    min={1} 
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div className="pt-6 border-t border-slate-100">
                <label className="block text-sm font-semibold text-slate-900 mb-4">Cover Image</label>
                <div className="flex items-center gap-6">
                  {(imagePreview || eventForm.coverImage) && (
                    <img src={imagePreview || eventForm.coverImage} alt="Cover" className="w-32 h-32 object-cover rounded-lg border border-slate-200 bg-slate-50" />
                  )}
                  <label className="cursor-pointer bg-white border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-50 transition text-sm font-medium text-slate-700 shadow-sm">
                    <span>{uploadingImage ? 'Uploading...' : 'Choose Image'}</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} disabled={uploadingImage} />
                  </label>
                </div>
              </div>

              {/* Tags */}
              <div className="pt-6 border-t border-slate-100">
                <label className="block text-sm font-semibold text-slate-900 mb-2">Tags (comma-separated)</label>
                <input
                  name="tags"
                  value={eventForm.tags}
                  onChange={handleEventChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" 
                  placeholder="music, concert, live"
                />
              </div>

              {/* Status & Submit */}
              <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                <div className="w-48">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Initial Status</label>
                  <select
                    name="status"
                    value={eventForm.status}
                    onChange={handleEventChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition text-sm"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="PUBLISHED">Published</option>
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/center/events')}
                    className="px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="fixed bottom-4 right-4 lg:hidden z-40 w-14 h-14 bg-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center"
      >
        <Bars3Icon className="w-6 h-6" />
      </button>
    </div>
  )
}

export default EventFormPage

